import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule, ToastController } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { PremiumChatService } from '../../../services/api/premium-chat.service';
import { StripeService } from '../../../services/stripe.service';
import { TranslateConfigService } from '../../../services/translate-config.service';
import { BrowserService } from '../../../services/browser.service';
import { DialogComponent } from '../../dialog/dialog.component';

/**
 * Diálogo obligatorio para el propietario del objeto cuando el chat está bloqueado.
 *
 * Presenta dos opciones colapsables:
 *  1. «Coordinar por chat la entrega» → pago premium (Stripe).
 *  2. «Continuar sin usar el chat» → envío de un único mensaje gratuito.
 *
 * El diálogo no se puede cerrar mientras el chat esté bloqueado:
 * si el usuario lo cierra (backdrop / back button), se emite `dialogDismissed`
 * para que el padre navegue fuera de la página de chat.
 */
@Component({
  selector: 'app-contact-finder-dialog',
  templateUrl: './contact-finder-dialog.component.html',
  styleUrls: ['./contact-finder-dialog.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, TranslateModule, DialogComponent],
})
export class ContactFinderDialogComponent implements OnChanges, OnDestroy {
  /** Controla la visibilidad del modal. */
  @Input() show = false;
  /** ID de la conversación actual. */
  @Input() conversationId!: number;
  /** Importe del chat premium (€). */
  @Input() premiumChatAmount = 0;
  /** Contenido del mensaje gratuito ya enviado (si existe). */
  @Input() ownerFreeMessage = '';
  /** Indica si el mensaje gratuito ya fue consumido. */
  @Input() ownerFreeMsgConsumed = false;
  /** Contenido de la respuesta del finder (si ya respondió). */
  @Input() finderFreeMessage = '';

  /** Se emite cuando el pago premium se completa con éxito. */
  @Output() premiumChatUnlocked = new EventEmitter<void>();
  /** Se emite con el contenido del mensaje gratuito al enviarlo. */
  @Output() messageSent = new EventEmitter<string>();
  /** Se emite cuando el usuario quiere ir a ajustes del perfil público. */
  @Output() publicProfileSettingsRequested = new EventEmitter<void>();
  /** Se emite cuando el modal se cierra (el padre debe navegar hacia atrás). */
  @Output() dialogDismissed = new EventEmitter<void>();

  /** Estado de las secciones colapsables. */
  chatSectionOpen = false;
  noChaSectionOpen = false;

  /** Texto del mensaje gratuito que escribe el propietario. */
  freeMessage = '';
  /** Flag de envío de mensaje en curso. */
  sendingMessage = false;
  /** Flag de pago en curso. */
  paymentLoading = false;
  /** Flag para mostrar el modal de confirmación de pago premium. */
  showPaymentConfirm = false;
  /** Flag de aceptación de términos en el modal de confirmación. */
  termsAccepted = false;

  /** Intro copy shown only when the owner has not written a message yet. */
  readonly noChatIntroLines = [
    {
      key: 'CONTACT_FINDER_DIALOG.NO_CHAT_INSTRUCTION',
      fallback: 'Escribe un mensaje a la persona que encontró el objeto.',
    },
    {
      key: 'CONTACT_FINDER_DIALOG.NO_CHAT_ADDITIONAL_1',
      fallback: 'Puedes indicar cómo te gustaría recuperarlo (lugar y horario).',
    },
    {
      key: 'CONTACT_FINDER_DIALOG.NO_CHAT_ADDITIONAL_2',
      fallback: 'También puedes proponer recogerlo en persona o en un punto oficial (policía u oficina de objetos perdidos).',
    },
  ] as const;

  /** Guidance rows that mirror the visual language of the reference card. */
  readonly noChatHighlights = [
    {
      icon: 'shield-outline',
      key: 'CONTACT_FINDER_DIALOG.SECURITY_WARNING',
      fallback: 'Por seguridad, no compartas teléfonos ni datos personales.',
    },
    {
      icon: 'handshake-outline',
      key: 'CONTACT_FINDER_DIALOG.FREE_CHANNEL_INFO',
      fallback: 'Este canal permite coordinar la devolución <b>sin coste.</b>',
    },
    {
      icon: 'chatbubble-ellipses-outline',
      key: 'CONTACT_FINDER_DIALOG.NO_CHAT_BULLET_3',
      fallback: 'Puedes enviar un mensaje y recibir una respuesta del finder.',
    },
    {
      icon: 'lock-closed-outline',
      key: 'CONTACT_FINDER_DIALOG.NO_CHAT_BULLET_4',
      fallback: 'La comunicación es <b>anónima</b> y se limita a un único intercambio.',
    },
  ] as const;

  /** Límite máximo de caracteres del mensaje gratuito. */
  readonly MAX_MESSAGE_LENGTH = 150;

  /** Timeout fallback id to reset sendingMessage if parent doesn't respond */
  private _sendFallbackTimer: any = null;

  /** Devuelve los caracteres restantes disponibles. */
  get remainingChars(): number {
    return this.MAX_MESSAGE_LENGTH - (this.freeMessage?.length ?? 0);
  }

  /**
   * The no-chat option should start open when there is already a sent message
   * or a finder response, so the user can see the existing conversation state.
   */
  get shouldOpenNoChatSectionByDefault(): boolean {
    return !!(
      this.ownerFreeMessage?.trim()
      || this.ownerFreeMsgConsumed
      || this.finderFreeMessage?.trim()
    );
  }

  /** Indica si el campo de mensaje debe mostrarse en modo solo-lectura. */
  get isMessageReadOnly(): boolean {
    return this.ownerFreeMsgConsumed;
  }

  /** Indica si el finder ya ha enviado una respuesta. */
  get hasFinderResponse(): boolean {
    return !!this.finderFreeMessage?.trim();
  }

  constructor(
    private premiumChatService: PremiumChatService,
    private stripeService: StripeService,
    private toastController: ToastController,
    private browserService: BrowserService,
    public t: TranslateConfigService,
  ) {}

  // ─── Secciones colapsables ────────────────────────────────────

  toggleChatSection(): void {
    this.chatSectionOpen = !this.chatSectionOpen;
  }

  ngOnDestroy(): void {
    if (this._sendFallbackTimer) {
      clearTimeout(this._sendFallbackTimer);
      this._sendFallbackTimer = null;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If the parent marks the free message as consumed, clear local sending state
    if (changes['ownerFreeMsgConsumed'] && this.ownerFreeMsgConsumed) {
      this.sendingMessage = false;
      this.freeMessage = '';
      if (this._sendFallbackTimer) {
        clearTimeout(this._sendFallbackTimer);
        this._sendFallbackTimer = null;
      }
    }

    // When the dialog is (re)opened, ensure internal UI state is reset
    if (changes['show'] && this.show) {
      this.sendingMessage = false;
      this.paymentLoading = false;
      this.chatSectionOpen = false;
      this.noChaSectionOpen = this.shouldOpenNoChatSectionByDefault;
      // do not clear ownerFreeMessage here; it's controlled by parent
    }

    if (changes['ownerFreeMessage'] || changes['ownerFreeMsgConsumed'] || changes['finderFreeMessage']) {
      this.noChaSectionOpen = this.noChaSectionOpen || this.shouldOpenNoChatSectionByDefault;
    }
  }

  toggleNoChatSection(): void {
    this.noChaSectionOpen = !this.noChaSectionOpen;
  }

  // ─── Pago premium ────────────────────────────────────────────

  /**
   * Opens the payment confirmation dialog before proceeding to Stripe.
   */
  requestPay(): void {
    this.showPaymentConfirm = true;
  }

  cancelPaymentConfirm(): void {
    this.showPaymentConfirm = false;
    this.termsAccepted = false;
  }

  confirmPay(): void {
    this.showPaymentConfirm = false;
    this.termsAccepted = false;
    this.pay();
  }

  /** Opens Terms & Conditions in an external browser. */
  openTerms(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.classList.contains('terms-link')) {
      event.preventDefault();
      event.stopPropagation();
      this.browserService.openTermsAndConditions();
    }
  }

  openPublicProfileSettings(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.cancelPaymentConfirm();
    this.publicProfileSettingsRequested.emit();
    this.dialogDismissed.emit();
  }

  private async ensureChatStillLocked(): Promise<boolean> {
    const status = await firstValueFrom(this.premiumChatService.getAmount(this.conversationId));

    if (status.success && status.premium_chat_unlocked) {
      await this.showToast(
        this.t.getItemTranslated('PREMIUM_CHAT.ALREADY_UNLOCKED')
          || this.t.getItemTranslated('DETAILS.PAYMENT_ALREADY_PAID')
          || 'El chat premium ya está desbloqueado',
        'warning',
      );
      this.premiumChatUnlocked.emit();
      return false;
    }

    return true;
  }

  async pay(): Promise<void> {
    if (this.paymentLoading) return;
    if (!(await this.ensureChatStillLocked())) return;
    this.paymentLoading = true;

    try {
      const paymentResult = await this.stripeService.executeCustomFlow({
        prepareNative: async () => {
          const res = await firstValueFrom(
            this.premiumChatService.createIntent(this.conversationId),
          );
          if (!res.success) {
            throw new Error('Failed to create premium chat intent');
          }
          return {
            reference: res.tokenRef,
            options: {
              paymentIntentClientSecret: res.paymentIntent,
              customerId: res.customer,
              customerEphemeralKeySecret: res.ephemeralKey,
              merchantDisplayName: 'Foundek',
            },
          };
        },
        prepareWeb: async () => {
          const res = await firstValueFrom(
            this.premiumChatService.createCheckoutSession(this.conversationId),
          );
          if (!res.success) {
            throw new Error('Failed to create premium chat checkout session');
          }
          return {
            reference: res.session.id,
            clientSecret: res.session.client_secret,
          };
        },
        loadingKey: 'COMMON.LOADING',
        processingKey: 'PREMIUM_CHAT.PROCESSING',
        errorKey: 'PREMIUM_CHAT.ERROR',
      });

      const verifiedResult = await this.stripeService.verifyPaymentOutcome({
        initialResult: paymentResult,
        verify: async () => firstValueFrom(this.premiumChatService.getAmount(this.conversationId)),
        isVerified: (status) => status.success && status.premium_chat_unlocked === true,
      });

      if (verifiedResult.success) {
        await this.showToast(
          this.t.getItemTranslated('PREMIUM_CHAT.PAYMENT_SUCCESS') || 'Premium chat unlocked!',
          'success',
        );
        this.premiumChatUnlocked.emit();
        return;
      }

      if (!verifiedResult.canceled) {
        throw verifiedResult.error ?? new Error('PREMIUM_CHAT.ERROR');
      }
    } catch (err: unknown) {
      const errMsg = (err as Error)?.message ?? '';
      if (errMsg !== 'Payment canceled' && errMsg !== 'WEB_CHECKOUT_CLOSED') {
        await this.showToast(
          this.t.getItemTranslated('PREMIUM_CHAT.ERROR') || 'An error occurred',
          'danger',
        );
      }
    } finally {
      this.paymentLoading = false;
    }
  }

  // ─── Mensaje gratuito ────────────────────────────────────────

  sendFreeMessage(): void {
    if (this.ownerFreeMsgConsumed) return;
    if (this.sendingMessage) return;
    const trimmed = this.freeMessage?.trim() ?? '';
    if (!trimmed) return;

    this.sendingMessage = true;

    // Fallback: reset sending state if parent doesn't respond within 10s
    if (this._sendFallbackTimer) clearTimeout(this._sendFallbackTimer);
    this._sendFallbackTimer = setTimeout(() => {
      this.sendingMessage = false;
      this._sendFallbackTimer = null;
    }, 10000);

    this.messageSent.emit(trimmed);
  }

  // ─── Dismiss del modal ────────────────────────────────────────

  /**
   * Handler del evento `ionModalDidDismiss`.
   * Como el diálogo es obligatorio mientras el chat esté bloqueado,
   * indicamos al padre que cierre la página de chat.
   */
  onDidDismiss(): void {
    if (!this.show) {
      return;
    }
    this.dialogDismissed.emit();
  }

  /**
   * Guard de `canDismiss` para el modal.
   * Siempre permite el dismiss (devuelve true), pero el padre
   * reaccionará al evento `dialogDismissed` navegando atrás.
   */
  canDismissHandler = async (): Promise<boolean> => {
    return true;
  };

  // ─── Utilidades ───────────────────────────────────────────────

  private async showToast(message: string, color: string): Promise<void> {
    const toast = await this.toastController.create({
      message,
      duration: 3000,
      color,
      position: 'top',
    });
    await toast.present();
  }
}
